import { useEffect, useState, useRef } from "react";
import BoxCard from "shared/components/box/BoxCard.js";
import BoxLogin from "shared/components/box/BoxLogin.js";
import PopupPremiumInfo from "shared/components/popups/PopupPremiumInfo.js";
import BoxMessage from "shared/components/box/BoxMessage.js";
import ButtonLogin from "shared/components/buttons/ButtonLogin.js";
import ChartBarGoalDifference from "shared/components/charts/ChartBarGoalDifference";
import ControllerClubs2 from "shared/components/controllers/ControllerClubs2";
import ItemRowMatch from "shared/components/items/ItemRowMatch.js";
import ItemRowPlayerWithEnergy from "shared/components/items/ItemRowPlayerWithEnergy.js";
import PopupInformationFormationMeta from "shared/components/popups/PopupInformationFormationMeta.js";
import PopupInformationResultProbability from "shared/components/popups/PopupInformationResultProbability.js";
import LoadingSquare from "shared/components/loading/LoadingSquare.js";
import MiscCompetitionRanking from "shared/components/misc/MiscCompetitionRanking";
import { getClubs, getFullCompetitions, getMatches, getOverallVsGdRates } from "shared/services/api-assistant.js";
import { isUserPremium } from "shared/utils/user.js";
import "./../bars/MenuTop.css";
import "./PageMatchPlanner.css";
import { getFormationMetas } from "shared/services/api-assistant.js";
import { getClubSquad } from "shared/services/api-mfl.js";
import { useNavigate } from "react-router-dom";
import { formations, formationToMfl } from "shared/utils/formation.js";
import { calculateTeamOverall } from "shared/utils/squad.js";
import MiscFootballFieldPlayer from "shared/components/misc/MiscFootballFieldPlayer";
import ButtonOnFieldPlayerView from "shared/components/buttons/ButtonOnFieldPlayerView.js";
import MatchPlannerSchedule from "../components/MatchPlannerSchedule.js";

const PageMatchPlanner = (props) => {
  const navigate = useNavigate();

  const [clubs, setClubs] = useState(null);
  const [selectedClub, setSelectedClub] = useState(null);
  const [matches, setMatches] = useState(null);
  const [competitions, setCompetitions] = useState(null);
  const [overallVsGdRates, setOverallVsGdRates] = useState(null);
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [matchesPerClubs, setMatchesPerClubs] = useState({});
  const [opponentSquads, setOpponentSquads] = useState({});
  const [formationMeta, setFormationMeta] = useState(null);
  const [selectedFormation, setSelectedFormation] = useState({});
  const [maxFatigue, setMaxFatigue] = useState(90);
  const [onFieldPlayerView, setOnFieldPlayerView] = useState("ovr");

  const requestIdRef = useRef(0);

  const fetchClubs = () => {
    getClubs({
      handleSuccess: (d) => {
        let c = d.data.getClubs.filter((club) => club.type !== "ARENA").sort((a, b) => a.division - b.division);
        setClubs(c);
        setSelectedClub(c[0]);
      },
      handleError: console.log,
      params: { owners: [props.assistantUser.id], considerStaffMembers: true },
    });
  };

  const fetchMatches = (requestId) => {
    const clubId = selectedClub.id;

    setMatches(null);

    getMatches({
      handleSuccess: (d) => {
        if (requestId !== requestIdRef.current) return;

        setMatches(d.data.getMatches.sort((a, b) => a.startDate.localeCompare(b.startDate)));
      },
      handleError: console.log,
      params: {
        limit: 100,
        clubs: [clubId],
        types: ["LEAGUE", "CUP"],
        currentSeason: true,
      },
    });
  };

  const fetchClubMatches = (clubId) => {
    getMatches({
      handleSuccess: (d) => {
        setMatchesPerClubs({ ...matchesPerClubs, ...{ [clubId]: d.data.getMatches } });
      },
      handleError: console.log,
      params: {
        limit: 100,
        clubs: [clubId],
        statuses: ["ENDED"],
        types: ["LEAGUE", "CUP"],
        currentSeason: true,
      },
    });
  };

  const fetchOpponentSquad = (clubId) => {
    getClubSquad({
      handleSuccess: (d) => {
        setOpponentSquads({ ...opponentSquads, ...{ [clubId]: d } });
      },
      handleError: console.log,
      params: {
        id: clubId,
      },
    });
  };

  const fetchFormationMeta = () => {
    if (!formationMeta) {
      getFormationMetas({
        handleSuccess: (d) => {
          setFormationMeta(d.data.getFormationMetas);
        },
        handleError: (e) => console.log(e),
        params: { engine: "16.0", difference: 22 },
      });
    }
  };

  const fetchClubStandings = (requestId) => {
    const clubId = selectedClub.id;
    setCompetitions(null);

    getFullCompetitions({
      handleSuccess: (v) => {
        if (requestId !== requestIdRef.current) return;

        setCompetitions(v.data.getCompetitions);
      },
      handleError: (e) => {
        if (requestId !== requestIdRef.current) return;

        console.log(`Error fetching standings for club ID ${clubId}:`, e);
      },
      params: {
        participants: [clubId],
      },
    });
  };

  const fetchOverallVsGdRates = () => {
    getOverallVsGdRates({
      handleSuccess: (d) => setOverallVsGdRates(d.data.getOverallVsGdRates),
      handleError: console.log,
      params: { engine: "15" },
    });
  };

  const countClubFormations = (matches, clubId) => {
    const formationCount = {};

    matches.forEach((match) => {
      // Check if the club is home
      if (match.homeClub.id === clubId && match.homeFormation) {
        const formation = match.homeFormation;
        formationCount[formation] = (formationCount[formation] || 0) + 1;
      }

      // Check if the club is away
      if (match.awayClub.id === clubId && match.awayFormation) {
        const formation = match.awayFormation;
        formationCount[formation] = (formationCount[formation] || 0) + 1;
      }
    });

    // Convert to array, sort by count descending, and back to object
    const sorted = Object.entries(formationCount)
      .sort((a, b) => b[1] - a[1])
      .reduce((acc, [formation, count]) => {
        acc[formation] = count;
        return acc;
      }, {});

    return sorted;
  };

  const getMatchCompetitions = (m) => {
    if (competitions.length === 0) return [];

    const maxSeason = Math.max(...competitions.map((c) => c.season));
    const filtered = competitions.filter((c) => c.season === maxSeason && c.type === m.type);
    return filtered.sort((a, b) => (a.type === "LEAGUE" ? -1 : b.type === "LEAGUE" ? 1 : 0));
  };

  const getCompetitionBlocks = (m, initialView) => {
    return getMatchCompetitions(m).map((competition) => (
        <MiscCompetitionRanking
          key={competition.id}
          competition={competition}
          initialView={initialView}
          clubToHighlight={selectedMatchId ? [m.homeClub.id, m.awayClub.id] : [selectedClub.id]}
          clubsToShow={selectedMatchId ? undefined : [m.homeClub.id, m.awayClub.id]}
          hideViewButton={!selectedMatchId}
        />
    ));
  };

  const getOverallDifference = (m) => {
    const diff = m.homeClub.id === selectedClub.id ? m.homeClub.b11 - m.awayClub.b11 : m.awayClub.b11 - m.homeClub.b11;

    if (diff === 0) return 0;
    return diff > 0 ? Math.ceil(diff / 5) * 5 : Math.floor(diff / 5) * 5;
  };

  useEffect(() => {
    if (props.assistantUser) fetchClubs();
  }, [props.assistantUser]);

  useEffect(() => {
    if (!props.assistantUser || !clubs || !selectedClub) return;

    const requestId = ++requestIdRef.current;

    fetchMatches(requestId);
    fetchClubStandings(requestId);
  }, [clubs, selectedClub?.id]);

  useEffect(() => {
    fetchOverallVsGdRates();
  }, []);

  if (!props.assistantUser) {
    return (
      <div className="d-flex h-100 justify-content-center align-items-center">
        <ButtonLogin
          flowUser={props.flowUser}
          assistantUser={props.assistantUser}
          logout={props.logout}
          content={<BoxLogin assistantUser={props.assistantUser} />}
        />
      </div>
    );
  }

  return (
    <div id="PageMatchPlanner" className="h-100 w-100">
      <nav className="MenuTop d-flex navbar">
        <h3 className="my-2 ps-5">
          <i className="bi bi-calendar-week-fill me-2"></i> Match planner
        </h3>
      </nav>

      <div className="h-100 w-100">
        <div className="container-fluid px-1 px-md-4 py-4">
          <div className="d-flex flex-column flex-md-row">
            <div className="flex-shrink-0">
              <BoxCard
                className="fit-content"
                title={"Clubs"}
                content={
                  <ControllerClubs2
                    clubs={clubs}
                    onSelect={(c) => {
                      setSelectedMatchId(null);
                      setSelectedClub(c);
                    }}
                    userId={props.assistantUser.id}
                    selectedClubId={selectedClub?.id}
                    excludeArena={true}
                  />
                }
              />
            </div>

            <div key={selectedMatchId ?? "match-list"} className="match-planner-view d-flex flex-column gap-3 p-2 w-100">
              {selectedMatchId && (
                <div className="d-flex justify-content-start">
                  <button className="btn btn-link btn-sm" onClick={() => setSelectedMatchId(null)}>
                    <i className="bi bi-caret-left-fill me-1" /> Back to match list
                  </button>
                </div>
              )}
              {matches &&
                competitions &&
                matches
                  .filter((m) => m.status === "PLANNED")
                  .filter((m) => !selectedMatchId || m.id === selectedMatchId)
                  .map((m) => {
                    const content = (
                      <div className="d-flex flex-column flex-md-grow-1 flex-md-shrink-1 g-3">
                        {!selectedMatchId && (
                          <div className="my-1 mb-3">
                            <ItemRowMatch match={m} />
                          </div>
                        )}

                        <div className={selectedMatchId ? "match-planner-details" : "row g-2"}>
                          {selectedMatchId && (
                            <div className="col-12">
                              <BoxCard
                                className="match-planner-summary m-0"
                                contentClassName="flex-column"
                                content={<ItemRowMatch match={m} />}
                              />
                            </div>
                          )}
                          {(selectedMatchId ? ["ranking"] : ["ranking", "projection"]).map((view) => (
                            <div
                              key={view}
                              className={selectedMatchId ? "match-planner-competition col-12 d-flex flex-column gap-3" : "col-12 col-md-6"}
                            >
                              {selectedMatchId ? (
                                <>
                                  <BoxCard
                                    className="m-0 flex-fill"
                                    title="Competition"
                                    content={<div className="w-100">{getCompetitionBlocks(m, view)}</div>}
                                  />
                                  {getMatchCompetitions(m).map((competition) => (
                                    <MatchPlannerSchedule
                                      key={competition.id}
                                      competition={competition}
                                      club={m.homeClub.id === selectedClub.id ? m.homeClub : m.awayClub}
                                      opponent={m.homeClub.id === selectedClub.id ? m.awayClub : m.homeClub}
                                      clubMatches={matches}
                                      selectedMatchId={selectedMatchId}
                                    />
                                  ))}
                                </>
                              ) : (
                                <div className="mx-1 px-2 py-2 background-color">
                                  <h6>{view === "ranking" ? "Ranking" : "Projection"}</h6>
                                  {getCompetitionBlocks(m, view)}
                                </div>
                              )}
                            </div>
                          ))}
                          {selectedMatchId && (
                            <>
                              <div className="match-planner-probability col-12">
                                <BoxCard
                                  className="m-0 h-100"
                                  contentClassName="match-planner-panel-content"
                                  title={
                                    <span className="d-inline-flex align-items-center">
                                      Result probability
                                      <span className="d-inline-flex fs-6">
                                        <PopupInformationResultProbability />
                                      </span>
                                    </span>
                                  }
                                  content={
                                    <div className="match-planner-probability-chart w-100">
                                      <ChartBarGoalDifference
                                        data={overallVsGdRates?.filter(
                                          (o) => o.overallDifference === Math.abs(getOverallDifference(m)),
                                        )}
                                        reverse={getOverallDifference(m) < 0}
                                      />
                                    </div>
                                  }
                                />
                              </div>
                              <div className="match-planner-formation col-12">
                                <BoxCard
                                  className="m-0 h-100"
                                  contentClassName="match-planner-panel-content"
                                  title={
                                    <span className="d-inline-flex align-items-center">
                                      Formation meta
                                      <span className="d-inline-flex fs-6">
                                        <PopupInformationFormationMeta />
                                      </span>
                                    </span>
                                  }
                                  actions={
                                    <button
                                      className="btn btn-link btn-small"
                                      onClick={() => navigate(`/formation-meta`)}
                                    >
                                      Formation meta <i className="bi bi-caret-right-fill"></i>
                                    </button>
                                  }
                                  content={
                                    <div className="match-planner-formation-content w-100">
                                      {(() => {
                                        let opponentClubId =
                                          m.homeClub.id === selectedClub.id ? m.awayClub.id : m.homeClub.id;

                                        return (
                                          <div className="match-planner-formation-table d-flex overflow-auto">
                                            {matchesPerClubs?.[opponentClubId] !== undefined ? (
                                              Object.keys(countClubFormations(matches, selectedClub.id)).length > 0 &&
                                              Object.keys(
                                                countClubFormations(matchesPerClubs[opponentClubId], opponentClubId),
                                              ).length > 0 ? (
                                                <table
                                                  className="formation-table text-xs"
                                                  cellPadding="5"
                                                  style={{
                                                    borderCollapse: "collapse",
                                                    width: "100%",
                                                  }}
                                                >
                                                  <thead>
                                                    <tr>
                                                      <th className="diag">
                                                        <span class="tl">You</span>
                                                        <span class="br">Opponent</span>
                                                      </th>
                                                      {Object.keys(countClubFormations(matches, selectedClub.id)).map(
                                                        (f) => (
                                                          <th className="text-center">
                                                            {f}
                                                            <br />
                                                            used {countClubFormations(matches, selectedClub.id)[f]}x
                                                          </th>
                                                        ),
                                                      )}
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {Object.keys(
                                                      countClubFormations(
                                                        matchesPerClubs[opponentClubId],
                                                        opponentClubId,
                                                      ),
                                                    ).map((k) => (
                                                      <tr>
                                                        <td className="text-center" style={{ minWidth: "90px" }}>
                                                          {k}
                                                          <br />
                                                          used{" "}
                                                          {
                                                            countClubFormations(
                                                              matchesPerClubs[opponentClubId],
                                                              opponentClubId,
                                                            )[k]
                                                          }
                                                          x
                                                        </td>
                                                        {Object.keys(countClubFormations(matches, selectedClub.id)).map(
                                                          (f) => {
                                                            let r = formationMeta
                                                              ?.filter((m) => m.formation1 === f && m.formation2 === k)
                                                              .pop();

                                                            if (r) {
                                                              return (
                                                                <td className="position-relative text-center">
                                                                  <span className="text-main">
                                                                    {Math.round(
                                                                      (r.victories /
                                                                        (r.victories + r.draws + r.defeats)) *
                                                                        100,
                                                                    ) + "%"}
                                                                  </span>
                                                                  {"/" +
                                                                    Math.round(
                                                                      (r.draws / (r.victories + r.draws + r.defeats)) *
                                                                        100,
                                                                    ) +
                                                                    "%/" +
                                                                    Math.round(
                                                                      (r.defeats /
                                                                        (r.victories + r.draws + r.defeats)) *
                                                                        100,
                                                                    ) +
                                                                    "%"}
                                                                  <div
                                                                    className="position-absolute"
                                                                    style={{
                                                                      right: "2px",
                                                                      bottom: "-2px",
                                                                      fontSize: "10px",
                                                                      color: "rgba(255, 255, 255, 0.3)",
                                                                    }}
                                                                  >
                                                                    {r.victories + r.draws + r.defeats}
                                                                  </div>
                                                                </td>
                                                              );
                                                            }
                                                            return <td>Not found</td>;
                                                          },
                                                        )}
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                              ) : (
                                                <BoxMessage content="No match found" />
                                              )
                                            ) : (
                                              <LoadingSquare />
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  }
                                />
                              </div>
                              <div className="col-12">
                                <BoxCard
                                  className="m-0"
                                  title="Lineup prediction"
                                  content={
                                    <div className="w-100">
                                      <div className="row">
                                        <div className="col-12 col-md-5">
                                          <div className="d-flex flex-row w-100">
                                            <div className="d-flex flex-fill mt-1">
                                              <select
                                                className={
                                                  "form-select mb-1 w-100 " +
                                                  ((selectedFormation[m.id] || "") === "" ? "highlighted" : "")
                                                }
                                                value={selectedFormation[m.id] || ""}
                                                onChange={(v) =>
                                                  setSelectedFormation({
                                                    ...selectedFormation,
                                                    [m.id]: v.target.value,
                                                  })
                                                }
                                                placeholder={"Formation"}
                                              >
                                                <option value={""} key={""}>
                                                  No formation
                                                </option>
                                                {Object.keys(formations).map((f) => (
                                                  <option value={f} key={f}>
                                                    {f}
                                                  </option>
                                                ))}
                                              </select>
                                            </div>
                                            <div className="d-flex flex-basis-80 ms-1">
                                              <input
                                                type="number"
                                                min="50"
                                                max="100"
                                                step="1"
                                                className="form-control w-100 my-1"
                                                value={maxFatigue}
                                                onChange={(v) => setMaxFatigue(parseInt(v.target.value))}
                                                placeholder={"Max Fatigue"}
                                              />
                                            </div>
                                            {selectedFormation[m.id] && (
                                              <div className="pt-1 mt-1 ms-2 pe-1">
                                                <button
                                                  className="btn btn-sm btn-info text-white mb-1"
                                                  onClick={() =>
                                                    setSelectedFormation({
                                                      ...selectedFormation,
                                                      [m.id]: undefined,
                                                    })
                                                  }
                                                >
                                                  <i className="bi bi-x-lg"></i>
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                          <div className="football-field football-field--responsive-players position-relative w-100 ratio ratio-1x1 mb-2">
                                            <div className="football-field-lines">
                                              <div className="halfway-line" />
                                              <div className="center-circle" />
                                              <div className="goal-box" />
                                            </div>
                                            {selectedFormation[m.id] ? (
                                              <div>
                                                <div className="position-absolute" style={{ top: "1px", right: "1px" }}>
                                                  <ButtonOnFieldPlayerView
                                                    selectedView={onFieldPlayerView}
                                                    onChange={(t) => setOnFieldPlayerView(t)}
                                                    hideDepth={true}
                                                  />
                                                </div>
                                                {calculateTeamOverall(
                                                  selectedFormation[m.id],
                                                  opponentSquads[
                                                    m.homeClub.id === selectedClub.id ? m.awayClub.id : m.homeClub.id
                                                  ]
                                                    ?.filter((p) => p.energy >= maxFatigue * 100)
                                                    .map((p) => ({
                                                      ...p,
                                                      player: p.metadata,
                                                    })),
                                                  true,
                                                )[1]
                                                  .filter((p) => p.position)
                                                  .map((p) => (
                                                    <div
                                                      key={p.position}
                                                      style={{
                                                        position: "absolute",
                                                        top: formations[selectedFormation[m.id]][p.position].y + "%",
                                                        left: formations[selectedFormation[m.id]][p.position].x + "%",
                                                        transform: "translate(-50%,-50%)",
                                                      }}
                                                    >
                                                      <MiscFootballFieldPlayer
                                                        teamMember={p}
                                                        formation={selectedFormation[m.id]}
                                                        onFieldPlayerView={onFieldPlayerView}
                                                      />
                                                    </div>
                                                  ))}
                                              </div>
                                            ) : (
                                              <div
                                                className={"d-flex flex-column flex-fill w-100 h-100 overflow-auto"}
                                                style={{ zIndex: 2, opacity: 0.9, backgroundColor: "#16191c" }}
                                              >
                                                <div
                                                  className={"Item no-hover d-flex flex-row flex-shrink-0 py-1 px-2"}
                                                >
                                                  <div className={"d-flex flex-grow-1"}>Formation</div>
                                                  <div className={"d-flex justify-content-center flex-basis-40"}>
                                                    OVR
                                                  </div>
                                                  <div className={"d-flex justify-content-center flex-basis-40"}>
                                                    Used
                                                  </div>
                                                  <div className={"d-flex flex-basis-40"}></div>
                                                </div>
                                                {opponentSquads[
                                                  m.homeClub.id === selectedClub.id ? m.awayClub.id : m.homeClub.id
                                                ] &&
                                                  Object.keys(formations)
                                                    .map((f) => {
                                                      const overall = calculateTeamOverall(
                                                        f,
                                                        opponentSquads[
                                                          m.homeClub.id === selectedClub.id
                                                            ? m.awayClub.id
                                                            : m.homeClub.id
                                                        ]
                                                          ?.filter((p) => p.energy >= maxFatigue * 100)
                                                          .map((p) => ({
                                                            ...p,
                                                            player: p.metadata,
                                                          })),
                                                      )[0];
                                                      return { formation: f, overall };
                                                    })
                                                    .sort((a, b) => b.overall - a.overall) // Sort by overall in descending order
                                                    .map(({ formation, overall }) => (
                                                      <div
                                                        className={
                                                          "Item no-hover d-flex flex-row flex-grow-1 flex-shrink-0 align-items-center px-2"
                                                        }
                                                        key={formation}
                                                      >
                                                        <div className={"d-flex flex-grow-1"}>{formation}</div>
                                                        <div className={"d-flex justify-content-center flex-basis-40"}>
                                                          {overall}
                                                        </div>
                                                        <div className={"d-flex justify-content-center flex-basis-40"}>
                                                          {matchesPerClubs[
                                                            m.homeClub.id === selectedClub.id
                                                              ? m.awayClub.id
                                                              : m.homeClub.id
                                                          ]
                                                            ? countClubFormations(
                                                                matchesPerClubs[
                                                                  m.homeClub.id === selectedClub.id
                                                                    ? m.awayClub.id
                                                                    : m.homeClub.id
                                                                ],
                                                                m.homeClub.id === selectedClub.id
                                                                  ? m.awayClub.id
                                                                  : m.homeClub.id,
                                                              )[formationToMfl[formation]] || "0"
                                                            : "?"}
                                                          x
                                                        </div>
                                                        <div className={"d-flex justify-content-end flex-basis-40"}>
                                                          <button
                                                            className="btn btn-xs btn-info text-white"
                                                            onClick={() =>
                                                              setSelectedFormation({
                                                                ...selectedFormation,
                                                                [m.id]: formation,
                                                              })
                                                            }
                                                          >
                                                            Select
                                                          </button>
                                                        </div>
                                                      </div>
                                                    ))}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        <div className="col-12 col-md-7 mt-1">
                                          {opponentSquads[
                                            m.homeClub.id === selectedClub.id ? m.awayClub.id : m.homeClub.id
                                          ] ? (
                                            opponentSquads[
                                              m.homeClub.id === selectedClub.id ? m.awayClub.id : m.homeClub.id
                                            ]
                                              .sort((a, b) => b.metadata.overall - a.metadata.overall)
                                              .map((p) => (
                                                <ItemRowPlayerWithEnergy
                                                  p={p}
                                                  isSelected={
                                                    selectedFormation[m.id] &&
                                                    calculateTeamOverall(
                                                      selectedFormation[m.id],
                                                      opponentSquads[
                                                        m.homeClub.id === selectedClub.id
                                                          ? m.awayClub.id
                                                          : m.homeClub.id
                                                      ]
                                                        ?.filter((p) => p.energy >= maxFatigue * 100)
                                                        .map((p) => ({
                                                          ...p,
                                                          player: p.metadata,
                                                        })),
                                                      true,
                                                    )[1].filter((t) => t.position && t.id === p.id).length > 0
                                                  }
                                                />
                                              ))
                                          ) : (
                                            <LoadingSquare height={"100%"} />
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  }
                                />
                              </div>
                            </>
                          )}

                          <div className="col-12">
                            <div className="d-flex flex-fill justify-content-end">
                              {(() => {
                                const matchStart = new Date(m.startDate);
                                const unlockTime = new Date(matchStart.getTime() - 12 * 60 * 60 * 1000);
                                const formattedUnlockTime =
                                  `${String(unlockTime.getMonth() + 1).padStart(2, "0")}-` +
                                  `${String(unlockTime.getDate()).padStart(2, "0")} ` +
                                  `${String(unlockTime.getHours()).padStart(2, "0")}:` +
                                  `${String(unlockTime.getMinutes()).padStart(2, "0")}`;

                                if (!isUserPremium(props.assistantUser) && unlockTime > new Date()) {
                                  return (
                                    <PopupPremiumInfo
                                      trigger={
                                        <button className="btn btn-premium btn-small" style={{ textAlign: "left" }}>
                                          <i className="bi bi-lock-fill"></i> Premium required
                                        </button>
                                      }
                                    />
                                  );
                                }

                                return !selectedMatchId ? (
                                  <button
                                    className="btn btn-link btn-small text-white"
                                    onClick={() => {
                                      setSelectedMatchId(m.id);
                                      fetchFormationMeta();
                                      fetchClubMatches(
                                        m.homeClub.id === selectedClub.id ? m.awayClub.id : m.homeClub.id,
                                      );
                                      fetchOpponentSquad(
                                        m.homeClub.id === selectedClub.id ? m.awayClub.id : m.homeClub.id,
                                      );
                                    }}
                                  >
                                    Open match <i className="bi bi-caret-right-fill"></i>
                                  </button>
                                ) : null;
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    );

                    return selectedMatchId ? <div key={m.id}>{content}</div> : <BoxCard key={m.id} content={content} />;
                  })}

              {matches !== null && matches.filter((m) => m.status === "PLANNED").length === 0 && (
                <BoxCard
                  className="w-100"
                  content={
                    <div style={{ height: "300px" }} className="w-100">
                      <BoxMessage content="No match found" />
                    </div>
                  }
                />
              )}

              {selectedClub &&
                (matches === null || (matches.filter((m) => m.status === "PLANNED").length > 0 && !competitions)) && (
                  <BoxCard className="w-100" content={<LoadingSquare height={"300px"} />} />
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageMatchPlanner;
